import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Zap, Monitor, Smartphone, ArrowRight, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { isAxiosError } from 'axios'
import { Link } from 'react-router-dom'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import { auditsApi, anonymousAuditsApi } from '@/api/client'
import type { DeviceType } from '@/api/client'
import { useSettingsStore } from '@/store/settings'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { getDesktopBridge } from '@/lib/desktop'
import { isLikelyPrivateTarget } from '@/lib/localTarget'

export default function HomePage() {
  const navigate = useNavigate()
  const defaultDevice = useSettingsStore(s => s.defaultDevice)
  const { data: currentUser } = useCurrentUser()
  const isAnonymous = !currentUser
  const [url, setUrl] = useState('')
  const [device, setDevice] = useState<DeviceType>(defaultDevice)

  const desktop = getDesktopBridge()
  const [runLocallyChoice, setRunLocallyChoice] = useState<boolean | null>(null)
  const runLocally = desktop !== null && !isAnonymous && (runLocallyChoice ?? isLikelyPrivateTarget(url))

  // Two mutations, two destinations. Branching here (rather than later in
  // handleSubmit) keeps onSuccess routing trivial — each mutation has exactly
  // one downstream view.
  const authedMutation = useMutation({
    mutationFn: auditsApi.start,
    onSuccess: data => navigate(`/audit/${data.auditId}`),
    onError: (error: unknown) => {
      const code = isAxiosError(error) ? (error.response?.data as { code?: string } | undefined)?.code : undefined
      if (code === 'TARGET_UNREACHABLE' && desktop !== null) {
        toast.error('The Lens server can\'t reach that address. Tick "Run on this machine" to audit it here.')
        setRunLocallyChoice(true)
        return
      }
      toast.error('Failed to start audit. Is the BFF running?')
    },
  })
  const anonMutation = useMutation({
    mutationFn: anonymousAuditsApi.start,
    onSuccess: data => navigate(`/audit-demo/${data.auditId}`),
    onError: () => toast.error('Failed to start audit. Is the BFF running?'),
  })
  const mutation = isAnonymous ? anonMutation : authedMutation

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) return

    if (isAnonymous) {
      // Anonymous flow: URL + device only. No source attachment, no AI, no save.
      anonMutation.mutate({ url: url.trim(), device })
      return
    }

    if (runLocally) {
      navigate('/audit-local', { state: { url: url.trim(), device } })
      return
    }

    authedMutation.mutate({ url: url.trim(), device })
  }

  return (
    <PageTransition>
      <div className="grid-bg min-h-[calc(100vh-56px)] flex flex-col items-center justify-center px-4 py-16">
        {/* Hero */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 blur-2xl bg-accent/30 rounded-full scale-150" />
              <Zap size={56} className="relative text-accent" fill="currentColor" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-3 tracking-tight">
            <span className="text-gradient">Lens</span>
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Audit any website for performance, accessibility, and best practices.
          </p>
        </motion.div>

        {/* Input card */}
        <motion.div
          className="w-full max-w-2xl"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <GlassCard>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* URL input */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Website URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-mono bg-white/5 border border-white/10 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-accent/60 focus:outline-none transition-colors"
                />
              </div>

              {/* Device toggle */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Device
                </label>
                <div className="flex gap-2">
                  {(['DESKTOP', 'MOBILE'] as DeviceType[]).map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDevice(d)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ${
                        device === d
                          ? 'bg-accent/15 border-accent/40 text-accent'
                          : 'bg-white/5 border-white/10 text-[var(--text-secondary)] hover:border-white/20'
                      }`}
                    >
                      {d === 'DESKTOP' ? <Monitor size={14} /> : <Smartphone size={14} />}
                      {d.charAt(0) + d.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Run locally toggle — desktop app, signed-in users only. */}
              {desktop !== null && !isAnonymous && (
                <label className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={runLocally}
                    onChange={e => setRunLocallyChoice(e.target.checked)}
                    className="mt-0.5 accent-accent"
                  />
                  <span>
                    <span className="block text-sm font-medium text-[var(--text-primary)]">
                      Run on this machine
                    </span>
                    <span className="block text-xs" style={{ color: 'var(--text-secondary)' }}>
                      For localhost and private-network sites the Lens server can't reach.
                    </span>
                  </span>
                </label>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={mutation.isPending || !url.trim()}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold bg-accent hover:bg-accent-dark text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed accent-glow"
              >
                {mutation.isPending ? (
                  <><Loader2 size={16} className="animate-spin" /> Starting audit…</>
                ) : (
                  <>Run Audit <ArrowRight size={16} /></>
                )}
              </button>

              {mutation.isError && (
                <p className="text-xs text-red-400 text-center">
                  Failed to start audit — the BFF didn't respond. Try again, or
                  check that the service is reachable.
                </p>
              )}

              {isAnonymous && (
                <p className="text-[11px] text-center" style={{ color: 'var(--text-muted)' }}>
                  Demo audit — won't be saved.{' '}
                  <Link to="/login" className="text-accent hover:underline">Sign in</Link>{' '}
                  to save reports and unlock AI suggestions.
                </p>
              )}
            </form>
          </GlassCard>
        </motion.div>
      </div>
    </PageTransition>
  )
}
